/**
 * 참여자 식별 코드 생성기.
 *
 * 코드 형식 정책:
 * - 6자리 고정 길이.
 * - 대문자 영문 + 숫자 사용. 사람이 받아 적거나 외워서 입력하기 좋은 길이로 선택.
 * - 시각적으로 헷갈리는 문자(`0`, `1`, `I`, `O`)는 제외하여 다른 디바이스에서 손으로
 *   옮겨 적을 때 발생할 수 있는 입력 오류를 방지한다.
 *
 * 코드 공간: 32^6 ≈ 약 10억 — 이벤트 운영 규모에선 충돌이 사실상 발생하지 않으나,
 * 서버에서 발급할 때는 추가로 중복 검사 후 재시도하는 것을 권장한다.
 */

/**
 * 참여자 코드에 사용 가능한 문자 집합.
 * `0`(숫자 영) / `O`(영문 오) / `1`(숫자 일) / `I`(영문 아이)는 의도적으로 제외했다.
 * 길이는 32자로 Base32와 동일한 정보 밀도를 가진다.
 */
export const PARTICIPANT_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' as const;

/** 참여자 코드 고정 길이. */
export const PARTICIPANT_CODE_LENGTH = 6;

/**
 * 참여자 코드 형식 정규식.
 * 6자리, 대문자/숫자, `0/1/I/O` 제외.
 *
 * @see PARTICIPANT_CODE_ALPHABET
 */
export const PARTICIPANT_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

/**
 * 암호학적으로 안전한 난수를 사용해 참여자 코드 1개를 생성한다.
 *
 * 브라우저의 `crypto.getRandomValues`를 사용해 균등한 분포를 보장하며,
 * Modulo bias를 피하기 위해 알파벳 길이가 2의 거듭제곱(32)이 되도록 설계했다.
 * 서버 사이드에서 호출될 일은 없으나, Node.js 18+ 환경에서도 글로벌
 * `crypto`로 동일하게 동작한다.
 *
 * @returns 6자리 대문자 영숫자 코드 (예: `K9R3HM`).
 */
export function generateParticipantCode(): string {
  const bytes = new Uint8Array(PARTICIPANT_CODE_LENGTH);
  crypto.getRandomValues(bytes);

  // 알파벳 길이는 32(2^5)이므로 한 바이트의 하위 5비트만 사용해도 균등 분포가 유지된다.
  let result = '';
  for (let i = 0; i < PARTICIPANT_CODE_LENGTH; i += 1) {
    const index = bytes[i]! & 0x1f;
    result += PARTICIPANT_CODE_ALPHABET[index];
  }
  return result;
}

/**
 * 사용자가 입력한 임의의 문자열을 참여자 코드 형식에 맞게 정규화한다.
 *
 * 처리 순서:
 * 1. 앞뒤 공백 제거 (`trim`).
 * 2. 대문자 변환 (`toUpperCase`) — 사용자 편의를 위해 소문자 입력도 허용.
 * 3. 대시·공백·언더스코어 제거 — 사용자가 가독성을 위해 임의로 끊어 적는 경우 흡수.
 *
 * 정규화 후에도 `PARTICIPANT_CODE_PATTERN`을 만족하지 않을 수 있으며,
 * 검증은 호출자(스키마 또는 API 레이어)에서 수행한다.
 *
 * @param raw - 사용자가 입력한 원본 문자열.
 * @returns 정규화된 코드 후보. 형식 검증은 별도로 수행해야 한다.
 */
export function normalizeParticipantCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s\-_]/g, '');
}

/**
 * 정규화된 문자열이 참여자 코드 형식을 만족하는지 확인하는 타입 가드성 헬퍼.
 *
 * 외부에서 별도의 정규식 import 없이 한 함수로 검증을 끝낼 수 있도록 분리했다.
 *
 * @param value - 검사할 문자열 (정규화 전후 무관).
 * @returns 형식이 올바르면 true.
 */
export function isValidParticipantCode(value: string): boolean {
  return PARTICIPANT_CODE_PATTERN.test(value);
}
